import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as LinkIcon, MapPin } from "lucide-mui";
import { useState } from "react";
import type { ContactProfile } from "../../../api/client.ts";
import { getCountryFlag } from "../../../components/CountryName.tsx";
import Panel from "../../../components/Panel.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import { getInitials } from "../../../components/UserAvatar.tsx";
import { formatDate, humanize } from "../../../utils/format.ts";
import { formatProfileDate, profileSourceLabel } from "./profileFormat.ts";

/** "c-level" → "C-level", "senior_ic" → "Senior ic": a provider's seniority as a label. */
function seniorityLabel(value: string): string {
  return value.includes("-") ? value.charAt(0).toUpperCase() + value.slice(1) : humanize(value);
}

// About text longer than this is folded behind "Show more".
const ABOUT_FOLD = 360;

interface ContactAboutCardProps {
  name: string;
  profile: ContactProfile;
  /** Off where a header above already shows the photo (the contact drawer). */
  showPhoto?: boolean;
}

/** Who the person is, as the sources we bought from describe them. The headline
 * is the page header's; this is the rest. */
export default function ContactAboutCard({ name, profile, showPhoto = true }: ContactAboutCardProps) {
  const [expanded, setExpanded] = useState(false);
  const about = profile.about ?? "";
  const folded = about.length > ABOUT_FOLD && !expanded;
  const place = profile.location || [profile.city, profile.region].filter(Boolean).join(", ");
  const country = profile.country ? `${getCountryFlag(profile.country)} ${profile.country}` : null;
  const links = Object.entries(profile.social_links);
  const newest = profile.sources[0];
  const started = formatProfileDate(profile.role_started_on);
  // The headline is in the page header; without anything else there is no card.
  const hasFacts =
    (showPhoto && profile.photo_url) || about || place || country || profile.seniority || profile.department ||
    started || profile.open_to_work || links.length > 0;
  if (!hasFacts) return null;

  return (
    <Panel
      title="About"
      subtitle={
        newest
          ? `From ${profile.sources.map((s) => profileSourceLabel(s.source)).join(", ")} · as of ${formatDate(newest.observed_at, "month")}`
          : undefined
      }
      sx={{ mb: 3 }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
        {showPhoto && (
          <Avatar src={profile.photo_url ?? undefined} alt={name} sx={{ width: 64, height: 64 }}>
            {getInitials(name)}
          </Avatar>
        )}
        <Stack spacing={1.25} sx={{ minWidth: 0, flexGrow: 1 }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            {(place || country) && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "text.secondary" }}>
                <MapPin fontSize="small" />
                <Typography variant="body2">{[place, country].filter(Boolean).join(" · ")}</Typography>
              </Stack>
            )}
            {profile.seniority && <Chip size="small" variant="outlined" label={seniorityLabel(profile.seniority)} />}
            {profile.department && <Chip size="small" variant="outlined" label={profile.department} />}
            {started && <Chip size="small" variant="outlined" label={`In role since ${started}`} />}
            {profile.open_to_work && <StatusChip kind="contact_signal" value="open_to_work" />}
          </Stack>
          {about && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-line",
                  ...(folded && {
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }),
                }}
              >
                {about}
              </Typography>
              {about.length > ABOUT_FOLD && (
                <Button size="small" onClick={() => setExpanded((v) => !v)} sx={{ px: 0, mt: 0.5 }}>
                  {expanded ? "Show less" : "Show more"}
                </Button>
              )}
            </Box>
          )}
          {links.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              {links.map(([platform, url]) => (
                <Chip
                  key={platform}
                  size="small"
                  variant="outlined"
                  icon={<LinkIcon />}
                  label={humanize(platform)}
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Panel>
  );
}
